import { Button } from '@authentic/loyalty-pwa/components';
import { TextField } from '@authentic/loyalty-pwa/components/Form';
import { UserServiceContext } from '@authentic/loyalty-pwa/providers';
import { objectIsEmpty } from '@authentic/loyalty-pwa/util';
import { FunctionalComponent, h, RenderableProps } from 'preact';
import { useContext, useEffect, useState } from 'preact/hooks';
import { Controller, useForm } from 'react-hook-form';

import { ErrorMessage } from '../../components/Error';
import { ChangePasswordContentStyled, ChangePasswordPageStyled, FormControlStyled, FormHint, FormStyled, FormTitleStyled, SignInButtons } from './change-password.style';
import { BackIcon } from '../../icons/Back';
import { link, Typography, Box } from '@authentic/loyalty-pwa/util';
import { Link } from 'preact-router/match';
import { Snackbar, SnackbarType } from '../../components/snackbar/snackbar';
import { DelTacoNav } from '../../containers/del-taco-nav/DelTacoNav';
import { DetectSamsungBrowser } from '../../lib/detect-samsung-browser';

export const ChangePasswordPage: FunctionalComponent = (props) => {
  const title = 'Change my Password';
  const [error, setError] = useState('');
  const [showSignInButtons, setShowSignInButtons] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isSamsungBrowser, setIsSamsungBrowser] = useState(false);
  const [openSnackbar, setOpenSnackbar] = useState(false);
  const [resetPasswordToken, setResetPasswordToken] = useState('');
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarType, setSnackbarType] = useState(SnackbarType.Success);
  const userService = useContext(UserServiceContext);

  const {
    handleSubmit,
    getValues,
    reset,
    setValue,
    control,
    formState: { errors, isDirty },
  } = useForm();

  useEffect(() => {
    const queryParams: any = props;
    setResetPasswordToken(queryParams.reset_password_token);
    setIsSamsungBrowser(DetectSamsungBrowser());
  });

  const onSubmit = (data: any) => {
    setIsLoading(true);
    setError('');
    userService
      ?.changePassword({
        password: data.password,
        passwordConfirmation: data.passwordConfirmation,
        resetPasswordToken: resetPasswordToken
      })
      .then((res) => {
        console.log('res changePassword api: ', res);
        if (res.success) {
          setSnackbarType(SnackbarType.Success);
          setSnackbarMessage('Password changed. Please sign in');
          setShowSignInButtons(true);
        } else {
          setSnackbarType(SnackbarType.Error);
          setSnackbarMessage(res.message);
        }
        setOpenSnackbar(true);
        resetForm();
        setIsLoading(false);
      });
  };

  const resetForm = () => {
    setValue('password', '');
    setValue('passwordConfirmation', '');
    setTimeout(() => {
      reset();
    }, 100);
  }

  const callbackOnCloseSnackbar = () => {
    setOpenSnackbar(false);
    console.log('callbackOnCloseSnackbar...');
  };

  const callbackOnClickSnackbar = () => {
    console.log('callbackOnClickSnackbar: ...');
  };

  return (
    <DelTacoNav title={title}>
      <ChangePasswordPageStyled>
        <ChangePasswordContentStyled>
          <FormTitleStyled>
            <Link href={link('/sign-in')}>
              <BackIcon></BackIcon>
            </Link>
            <span>{title}</span>
          </FormTitleStyled>
          <FormStyled onSubmit={handleSubmit(onSubmit)}>
            <FormHint>*Required Field</FormHint>
            <FormControlStyled mb={18}>
              <Controller
                render={({ field }: any): any => (
                  <TextField
                    error={errors?.password}
                    label="New Password"
                    variant="password"
                    type="password"
                    {...field}
                    required
                    id="password"
                  />
                )}
                rules={{
                  required: {
                    value: true,
                    message: 'Please enter your password',
                  },
                  minLength: {
                    value: 8,
                    message: 'Password must have at least 8 characters',
                  },
                  maxLength: {
                    value: 16,
                    message: 'Password must not be longer than 16 characters',
                  },
                }}
                control={control}
                name="password"
                defaultValue=""
              />
            </FormControlStyled>

            <FormControlStyled>
              <Controller
                render={({ field }: any): any => (
                  <TextField
                    error={errors?.passwordConfirmation}
                    label="Confirm Password"
                    variant="password"
                    type="password"
                    {...field}
                    required
                    id="passwordConfirmation"
                  />
                )}
                rules={{
                  required: {
                    value: true,
                    message: 'Please enter your password confirmation',
                  },
                  validate: (value) => value === getValues('password') || 'Passwords do not match',
                }}
                control={control}
                name="passwordConfirmation"
                defaultValue=""
              />
            </FormControlStyled>

            <Box mb={48}>
              <Typography variant="body3">
                Password must contain 8-16 characters and all of the following: 1 upper, 1 lower, & 1
                number.
              </Typography>
            </Box>
            {(!objectIsEmpty(errors) || error) && (
              <ErrorMessage>{error ? error : 'Review highlighted errors to continue'}</ErrorMessage>
            )}

            <Button ariaLabel="NEXT" type="submit" disabled={!isDirty || isLoading} fullWidth>
              SUBMIT
            </Button>

            {(showSignInButtons && (
              <SignInButtons>
                {(!isSamsungBrowser &&
                  <a href="https://dl.em.deltaco.com/?dl=sign_in">
                    <Button ariaLabel="Log into the Mobile App" fullWidth>
                      Log into the Mobile App
                    </Button>
                  </a>
                )}
                <Link href={link('/sign-in')}>
                  <Button ariaLabel="Log into the Web App" fullWidth>
                    Log into the Web App
                  </Button>
                </Link>
              </SignInButtons>
            ))}

          </FormStyled>
        </ChangePasswordContentStyled>
        <Snackbar
          message={snackbarMessage}
          isActive={openSnackbar}
          type={snackbarType}
          timeout={10000}
          callbackOnClose={callbackOnCloseSnackbar}
          callbackOnClick={callbackOnClickSnackbar}></Snackbar>
      </ChangePasswordPageStyled>
    </DelTacoNav>
  );
};
